import { User } from "@/store/modules/User/interface";
import { store } from "@/store/store";
import { Location_ } from "@/types/types";

class Utils {
  /**
   * this function allow you to verify if an email address is valid
   *
   * @param  {string} email
   * @returns boolean
   */
  static validateEmail = (email: string): boolean => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ) === null
      ? false
      : true;
  };

  /**
   * this function allow you to transform a date or a string date in formatted string with a specific delimiter
   *
   * @param  {string|Date} date
   * @param  {} delimiter="/"
   * @returns string
   */
  static transformDateToYMD = (date: string | Date, delimiter = "/"): string => {
    if (typeof date === "string") date = new Date(date);
    return `${Utils.formatDateNumber(date.getFullYear())}${delimiter}${Utils.formatDateNumber(
      date.getMonth() + 1
    )}${delimiter}${Utils.formatDateNumber(date.getDate())}`;
  };

  /**
   * this function allow you to transform a date or a string date in formatted string to a specific format
   * @param  {string|Date} date
   * @returns string
   */
  static transformDateToYMDHMS = (date: string | Date): string => {
    if (typeof date === "string") date = new Date(date);
    return `${date.getFullYear()}/${Utils.formatDateNumber(date.getMonth() + 1)}/${Utils.formatDateNumber(
      date.getDate()
    )} ${Utils.formatDateNumber(date.getHours())}:${Utils.formatDateNumber(date.getMinutes())}:${Utils.formatDateNumber(
      date.getSeconds()
    )}`;
  };

  /**
   * This function allow you to transform a date or a string date in formatted string to a specific format
   * @param  {string|Date} date
   * @returns string
   */
  static transformDateToHMS = (date: string | Date): string => {
    if (typeof date === "string") date = new Date(date);
    return `${Utils.formatDateNumber(date.getHours())}:${Utils.formatDateNumber(
      date.getMinutes()
    )}:${Utils.formatDateNumber(date.getSeconds())}`;
  };

  /**
   * this function allow you to transform a day, month or anything for a number to a string with a specific length
   *
   * @param  {number} number
   * @returns string
   */
  static formatDateNumber = (number: number): string => {
    return number < 10 ? `0${number}` : `${number}`;
  };

  /**
   * this function allow you to transform array of number into array of Location
   *
   * @param  {Array<number>} locations
   * @returns Array
   */
  static arrayToLocation = (locations: Array<number>): Array<Location_> | Location_ => {
    const locations_: Array<Location_> = [];
    if (locations.length === 2) return { longitude: locations[0], latitude: locations[1] };
    for (let x = 0; x < locations.length; x = x + 2) {
      if (locations[x + 1] !== undefined) {
        locations_.push({ longitude: locations[x], latitude: locations[x + 1] });
      }
    }
    return locations_;
  };

  /**
   * this function allow you to get the absolute difference between 2 dates
   *
   * @param  {number} date_timestanp_1
   * @param  {number} date_timestanp_2
   * @returns number
   */
  static getDifferenceBetweenDateAbs = (date_timestanp_1: number, date_timestanp_2: number): number => {
    return Math.abs(
      Math.ceil(date_timestanp_1 / (1000 * 60 * 60 * 24)) - Math.ceil(date_timestanp_2 / (1000 * 60 * 60 * 24))
    );
  };

  /**
   * this function allow you to get the difference in days between 2 dates
   *
   * @param  {number} date_timestanp_1
   * @param  {number} date_timestanp_2
   * @returns number
   */
  static getDifferenceBetweenDate = (date_timestanp_1: number, date_timestanp_2: number): number => {
    return Math.ceil(date_timestanp_1 / (1000 * 60 * 60 * 24)) - Math.ceil(date_timestanp_2 / (1000 * 60 * 60 * 24));
  };

  /**
   * this function allow you to get the difference in hours between 2 dates
   *
   * @param  {number} date_timestanp_1
   * @param  {number} date_timestanp_2
   * @returns number
   */
  static getDifferenceBetweenHours = (date_timestanp_1: number, date_timestanp_2: number): number => {
    return Math.ceil(date_timestanp_1 / (1000 * 60 * 60)) - Math.ceil(date_timestanp_2 / (1000 * 60 * 60));
  };

  /**
   * this function allow you to create a blob from a csv
   *
   * @param  {string} csv_string
   * @returns Blob
   */
  static createCsvBlob = (csv_string: string): Blob => {
    return new Blob([csv_string], { type: "text/csv;charset=utf-8" });
  };

  /**
   * this fonction create an hidden input, autoclick it then destroy it. it allow you to download a file without user action.
   *
   * @param  {string} url
   * @param  {string} filename
   * @returns void
   */
  static createAndClickHiddenInput = (url: string, filename: string): void => {
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * this function allow you to check if the logged user is admin or not
   *
   * @returns boolean
   */
  static isLoggedUserAdmin = (): boolean => {
    const user = store.getters.LOGGED_USER as User;
    return store.getters.ROLE_BY_ID[user.role].name.toUpperCase() === "ADMIN" ? true : false;
  };

  /**
   * this function allow you to transform a string boolean in real boolean
   *
   * @param  {string} value
   * @returns boolean
   */
  static transformStringBoolean = (value: string): boolean => {
    return value === "true" ? true : false;
  };

  /**
   * this function allow you to transform your string search param into an URLSearchParams
   *
   * @returns URLSearchParams
   */
  static getUrlParameters = (): URLSearchParams => {
    return new URLSearchParams(window.location.search);
  };

  static toRadians = (degree: number): number => {
    return (degree * Math.PI) / 180;
  };

  static toDegrees = (radians: number): number => {
    return (radians * 180) / Math.PI;
  };

  static calculateDistance = (lat1: number, long1: number, lat2: number, long2: number): number => {
    const R = 6371; // Radius of the earth in km
    const dLat = Utils.toRadians(lat2 - lat1); // deg2rad below
    const dLon = Utils.toRadians(long2 - long1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(Utils.toRadians(lat1)) * Math.cos(Utils.toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
  };

  // static distanceToCoordinates = (distance: number, latitude: number, longitude: number): Location_ => {
  //   const R = 6371; // Radius of the earth in km
  //   const d = distance / R;
  //   const lat = Utils.toRadians(latitude);
  //   const lon = Utils.toRadians(longitude);
  //   const dLat = d / Math.cos(lat);
  //   const dLon = d / Math.cos(lat);
  //   const lat2 = lat + dLat;
  //   const lon2 = lon + dLon;
  //   return { latitude: Utils.toDegrees(lat2), longitude: Utils.toDegrees(lon2) };
  // };

  static calculateAngle = (lat1: number, long1: number, lat2: number, long2: number): number => {
    const dLon = Utils.toRadians(long2 - long1);
    const lat1_ = Utils.toRadians(lat1);
    const lat2_ = Utils.toRadians(lat2);
    const y = Math.sin(dLon) * Math.cos(lat2_);
    const x = Math.cos(lat1_) * Math.sin(lat2_) - Math.sin(lat1_) * Math.cos(lat2_) * Math.cos(dLon);
    const brng = Utils.toDegrees(Math.atan2(y, x));
    return brng;
  };

  static calculateFromDistanceAndAngleCoordinates = (
    distance: number,
    angle: number,
    latitude: number,
    longitude: number
  ): Location_ => {
    const R = 6371; // Radius of the earth in km
    const d = distance / R;
    const lat = Utils.toRadians(latitude);
    const lon = Utils.toRadians(longitude);
    const dLat = d / Math.cos(lat);
    const dLon = d / Math.cos(lon);
    const lat3 = lat + dLat * Math.sin(Utils.toRadians(angle));
    const lon3 = lon + dLon * Math.cos(Utils.toRadians(angle));
    return { latitude: Utils.toDegrees(lat3), longitude: Utils.toDegrees(lon3) };
  };

  static transformDateToHour = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 0, 0, 0);
  };
}

export default Utils;
